<template>
  <form @submit.prevent="onSubmit">
    <h2>{{ t('user') }}</h2>

    <div class="mt-4 grid grid-cols-3 gap-4">
      <LabeledText v-if="user" :label="t('customerTier')">
        <CustomerTierBadge :customer-tier="user?.customerTier ?? CustomerTier.NORMAL" size="md" />
      </LabeledText>

      <LabeledText v-if="user?.creator" :label="t('createdBy')">
        {{ user.creator.firstName }} {{ user.creator.lastName }} ({{ user.creator.email }})
      </LabeledText>
      <SelectField
        name="role"
        :label="t('role')"
        :options="roleOptions"
        :disabled="!canEditField('role')"
        class="col-start-1"
      />
      <SelectField
        v-if="formValues.role === UserRole.AGENCY || formValues.role === UserRole.COMPANY"
        name="partnerSubTypeId"
        :label="t('partnerType')"
        :options="partnerSubTypeOptions"
        :disabled="!canEditField('partnerSubTypeId')"
        keep-value-on-unmount
      />
      <InputField name="company" :label="t('company')" :disabled="!canEditField('company')" />
      <InputField
        name="vatId"
        :label="t('vatId')"
        :disabled="!canEditField('vatId')"
        autocapitalize="characters"
        autocorrect="off"
        spellcheck="false"
      />
      <SelectField
        name="gender"
        :label="t('gender')"
        :options="genderOptions"
        :disabled="!canEditField('gender')"
        class="col-start-1"
      />
      <InputField name="firstName" :label="t('firstName')" :disabled="!canEditField('firstName')" />
      <InputField name="lastName" :label="t('lastName')" :disabled="!canEditField('lastName')" />
      <InputField name="email" :label="t('email')" :disabled="!canEditField('email')" />
      <InputField name="phone" :label="t('phone')" :disabled="!canEditField('phone')" />
      <template v-if="hasSip">
        <InputField name="sipId" :label="t('sipId')" :disabled="!canEditField('sipId')" />
        <InputField name="did" :label="t('did')" :disabled="!canEditField('did')" />
        <InputField name="callerId" :label="t('callerId')" :disabled="!canEditField('callerId')" />
      </template>
      <InputField
        v-if="formValues.role && employeeRoles.includes(formValues.role)"
        name="staffomaticUserId"
        :label="t('staffomaticUserId')"
        :disabled="!canEditField('staffomaticUserId')"
      />
      <DatepickerField
        v-if="formValues.role === UserRole.CUSTOMER"
        name="dateOfBirth"
        :label="t('dateOfBirth')"
        :disabled="!canEditField('dateOfBirth')"
        calendar-date
      />
      <div class="flex gap-x-2">
        <div>
          <SearchableSelectField
            :name="`country`"
            :label="t('country')"
            :options="countries"
            :track-by="['label', 'labelEn', 'labelShort', 'value', 'alternativeNames']"
            :disabled="!canEditField('country')"
          />
          <div
            v-if="formValues.country && !countries.some((c) => c.value === formValues.country)"
            class="text-bittersweet-600"
          >
            {{ t('invalidCountry') }}: {{ formValues.country }}
          </div>
        </div>
        <SelectField
          class="basis-1/2"
          name="preferredLocal"
          :label="t('preferredLocal')"
          :options="preferredLocalOptions"
          :disabled="!canEditField('preferredLocal')"
        />
      </div>
      <InputField
        class="w-full grow"
        name="street"
        :label="t('street')"
        :disabled="!canEditField('street')"
      />
      <div class="flex gap-2">
        <InputField
          class="basis-2/5"
          name="zip"
          :label="t('zip')"
          :disabled="!canEditField('zip')"
        />
        <InputField name="city" :label="t('city')" :disabled="!canEditField('city')" />
      </div>
    </div>

    <Divider />

    <div v-if="!isPublicUser">
      <MultiSelectField
        v-if="stations"
        name="stationIds"
        :label="t('stations')"
        :options="
          stations.map((station) => {
            return {
              label: getLocalizedField(station.info),
              value: station.id,
            };
          })
        "
        :disabled="!canEditField('stations')"
      />

      <Divider />
    </div>

    <TextareaField
      name="comment"
      :label="formValues.role === UserRole.COMPANY ? t('commentDetailed') : t('comment')"
      :disabled="!canEditField('comment')"
    />

    <Divider />

    <AgencySettingsForm
      :role="formValues.role"
      :partner-sub-type="selectedPartnerSubType"
      :disabled="!canEditField('agencySettings')"
    />

    <template v-if="formValues.role === UserRole.COMPANY">
      <div class="mt-4 grid grid-cols-3 gap-4">
        <SelectField
          name="agencySettings.acquisitionSource"
          :label="t('acquisitionSource')"
          :options="acquisitionSourceOptions"
          :disabled="!canEditField('agencySettings')"
        />
        <SelectField
          name="agencySettings.intendedRentalFrequency"
          :label="t('intendedRentalFrequency')"
          :options="intendedRentalFrequencyOptions"
          :disabled="!canEditField('agencySettings')"
        />
        <MultiSelectField
          name="agencySettings.requiredVehicleClasses"
          :label="t('requiredVehicleClasses')"
          :options="vehicleClassPreferenceOptions"
          :disabled="!canEditField('agencySettings')"
        />
      </div>
      <Divider />
    </template>

    <UserFunctionsForm
      v-if="formValues.role === UserRole.ADMIN"
      :user="user"
      :disabled="!canEditField('functions')"
    />

    <div class="mt-3 flex justify-end">
      <CVButton
        variant="default"
        :is-loading="isLoading || isValidatingVatId"
        :disabled="!canEditResource"
        @click="onSubmit"
        >{{ t('save') }}</CVButton
      >
    </div>
  </form>
</template>

<script lang="ts" setup>
import type { UpdateUserDto, User } from '@/entities/auth/user.entity';
import type { Station } from '@/entities/station/station.entity';
import { useI18n } from 'vue-i18n';
import { userEditSchema } from '@/validation/user.schema';
import { useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import { cloneDeep } from 'lodash';
import { usePreventLeavingUnsavedForm } from '@/hooks/use-prevent-leaving-unsaved-form';
import { useCanEditField } from '@/hooks/use-can-edit-field';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import { Gender } from '@/entities/auth/gender.enum';
import { Local } from '@/entities/auth/local.enum';
import DatepickerField from '@/components/DatepickerField.vue';
import AgencySettingsForm from '@/components/forms/AgencySettingsForm.vue';
import UserFunctionsForm from '@/components/forms/UserFunctionsForm.vue';
import MultiSelectField from '@/components/MultiSelectField.vue';
import Divider from '@/components/Divider.vue';
import { UserRole } from '@/entities/auth/user-role.enum';
import { formHasChanged } from '@/hooks/use-form-has-changed';
import TextareaField from '@/components/TextareaField.vue';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import { getCountries } from '@/utils/countries';
import SearchableSelectField from '@/components/SearchableSelectField.vue';
import LabeledText from '@/components/LabeledText.vue';
import CustomerTierBadge from '@/components/badges/CustomerTierBadge.vue';
import { CompanyInvoicingType, CustomerTier } from '@carvia/ros-client-types';
import CVButton from '@/components/buttons/CVButton.vue';
import { toTypedSchema } from '@vee-validate/yup';
import { useAbility } from '@casl/vue';
import { fromCalendarDate } from '@/utils/calendar-date';
import { usePartnerSubTypes } from '@/queries/use-users';
import { useVatIdValidation } from '@/hooks/use-vat-id-validation';
import {
  AcquisitionSource,
  IntendedRentalFrequency,
  VehicleClassPreference,
} from '@carvia/ros-client-types';

const props = defineProps<{
  user?: User;
  me: User;
  stations: Station[];
  isLoading: boolean;
  isFetching: boolean;
  leadPrefill?: {
    leadId: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    company?: string;
    role?: UserRole;
  };
}>();

const emit = defineEmits<{
  (e: 'onSubmit', value: UpdateUserDto): void;
  (e: 'refetch'): void;
}>();

const { t, locale } = useI18n();
const { getLocalizedField } = useLocalizedFields();

const countries = computed(() => getCountries(locale.value));
const ability = useAbility();

const validRoles = computed(() => {
  const rules = ability.rulesFor('create', 'User');
  return Object.values(UserRole).filter(
    (role) =>
      (role === props.user?.role ||
        (
          rules.find(
            (r) => r.conditions && 'role' in r.conditions && '$in' in (r.conditions.role as any),
          ) as any
        )?.conditions?.role?.['$in'].includes(role)) ??
      true,
  );
});

const initialValues = {
  company: props.user?.company ?? props.leadPrefill?.company,
  gender: props.user?.gender,
  firstName: props.user?.firstName ?? props.leadPrefill?.firstName,
  lastName: props.user?.lastName ?? props.leadPrefill?.lastName,
  email: props.user?.email ?? props.leadPrefill?.email,
  phone: props.user?.phone ?? props.leadPrefill?.phone,
  vatId: props.user?.vatId,
  sipId: props.user?.sipId ?? undefined,
  did: props.user?.did ?? undefined,
  callerId: props.user?.callerId ?? undefined,
  staffomaticUserId: props.user?.staffomaticUserId ?? undefined,
  street: props.user?.street ?? undefined,
  zip: props.user?.zip ?? undefined,
  city: props.user?.city ?? undefined,
  country: props.user ? (props.user.country ?? undefined) : 'DE',
  dateOfBirth: props.user?.dateOfBirth ? fromCalendarDate(props.user.dateOfBirth) : undefined,
  role:
    props.user?.role ??
    props.leadPrefill?.role ??
    (validRoles.value.includes(UserRole.OPERATOR) ? UserRole.OPERATOR : validRoles.value.at(0)),
  partnerSubTypeId: props.user?.partnerSubType?.id ?? undefined,
  stationIds: props.user?.stations.map((station) => station.id),
  preferredLocal: props.user?.preferredLocal ?? Local.DE_DE,
  preferredPaymentTerminalId: props.user?.preferredPaymentTerminal?.id,
  comment: props.user?.comment,
  agencySettings: props.user?.customAgencySettings,
  functions: props.user?.functions ?? [],
} satisfies UpdateUserDto;

const isPublicUser = computed(
  () =>
    formValues.role === UserRole.CUSTOMER ||
    formValues.role === UserRole.AGENCY ||
    formValues.role === UserRole.COMPANY,
);

const employeeRoles: UserRole[] = [
  UserRole.ADMIN,
  UserRole.HUB_MANAGER,
  UserRole.OPERATOR,
  UserRole.HUB_AGENT,
];

const createOptions = (enumObj: any, prefix: string) =>
  computed(() =>
    Object.values(enumObj).map((value) => ({
      label: t(`${prefix}.${value}`) as string,
      value: value as string,
    })),
  );

const genderOptions = createOptions(Gender, 'gender');

const roleOptions = computed(() =>
  validRoles.value.map((role) => ({
    label: t(`userRoles.${role}`),
    value: role,
  })),
);

const preferredLocalOptions = createOptions(Local, 'locales');
const acquisitionSourceOptions = createOptions(AcquisitionSource, 'acq');
const intendedRentalFrequencyOptions = createOptions(IntendedRentalFrequency, 'freq');
const vehicleClassPreferenceOptions = createOptions(VehicleClassPreference, 'vclass');
const { data: partnerSubTypes } = usePartnerSubTypes();

const partnerSubTypeOptions = computed(() =>
  (partnerSubTypes.value ?? [])
    .filter((partnerSubType) => partnerSubType.role === formValues.role)
    .map((partnerSubType) => ({
      label: getLocalizedField(partnerSubType.name),
      value: partnerSubType.id,
    })),
);

const selectedPartnerSubType = computed(() =>
  (partnerSubTypes.value ?? []).find(
    (partnerSubType) => partnerSubType.id === formValues.partnerSubTypeId,
  ),
);

const {
  handleSubmit,
  values: formValues,
  setFieldValue,
} = useForm<UpdateUserDto>({
  initialValues,
  validationSchema: toTypedSchema(userEditSchema),
});

const { canEditField, canEditResource } = useCanEditField(
  'User',
  () => (props.user ? { ...props.user, ...formValues } : formValues),
  () => (props.user ? 'update' : 'create'),
);

const hasSip = computed(
  () => !!formValues.role && [UserRole.OPERATOR, UserRole.ADMIN].includes(formValues.role),
);

watch(
  () => formValues.role,
  (role) => {
    if (role === UserRole.COMPANY) {
      setFieldValue('agencySettings', {
        ...formValues.agencySettings,
        companyInvoicingType: role === UserRole.COMPANY ? CompanyInvoicingType.REGULAR : undefined,
      });
    }

    if (role !== UserRole.AGENCY && role !== UserRole.COMPANY) {
      setFieldValue('partnerSubTypeId', null);
      return;
    }

    const currentPartnerSubType = (partnerSubTypes.value ?? []).find(
      (partnerSubType) => partnerSubType.id === formValues.partnerSubTypeId,
    );

    if (currentPartnerSubType?.role !== role) {
      const initialPartnerSubTypeId =
        props.user?.partnerSubType?.role === role ? props.user.partnerSubType.id : undefined;

      setFieldValue('partnerSubTypeId', initialPartnerSubTypeId);
    }
  },
);

watch(
  () => formValues.partnerSubTypeId,
  () => {
    const currentPartnerSubType = (partnerSubTypes.value ?? []).find(
      (partnerSubType) => partnerSubType.id === formValues.partnerSubTypeId,
    );
    setFieldValue('agencySettings', {
      ...formValues.agencySettings,
      ...(currentPartnerSubType?.agencySettings?.commissionPercent
        ? { commissionPercent: currentPartnerSubType.agencySettings.commissionPercent }
        : {}),
      ...(currentPartnerSubType?.agencySettings?.discountPercent
        ? { discountPercent: currentPartnerSubType.agencySettings.discountPercent }
        : {}),
      ...(currentPartnerSubType?.agencySettings?.noDeposit
        ? { noDeposit: currentPartnerSubType.agencySettings.noDeposit }
        : {}),
    });
  },
);

const unchangedValues = ref(
  props.leadPrefill && !props.user ? ({} as UpdateUserDto) : cloneDeep(formValues),
);

const { isValidatingVatId, validateVatId } = useVatIdValidation();

const onSubmit = handleSubmit(async (values) => {
  if (!(await validateVatId(values.vatId, props.user?.vatId, values.country))) return;
  unchangedValues.value = cloneDeep(formValues);
  emit('onSubmit', values);
});

usePreventLeavingUnsavedForm(() =>
  formHasChanged(
    { ...unchangedValues.value, agencySettings: unchangedValues.value.agencySettings ?? {} },
    { ...formValues, agencySettings: formValues.agencySettings ?? {} },
  ),
);
</script>

<i18n lang="json">
{
  "en": {
    "user": "User",
    "role": "Role",
    "partnerType": "Partner Type",
    "customer": "Customer",
    "createdBy": "Created by",
    "gender": "Gender",
    "firstName": "First Name",
    "lastName": "Last Name",
    "company": "Company",
    "vatId": "VAT ID",
    "email": "Email",
    "city": "City",
    "zip": "Zip",
    "street": "Street",
    "phone": "Phone",
    "country": "Country",
    "dateOfBirth": "Date Of Birth",
    "preferredLocal": "Pref. Language",
    "stations": "Stations",
    "comment": "Comment",
    "commentDetailed": "Comment (How did you win this customer? Short story)",
    "acquisitionSource": "Where does the customer come from?",
    "intendedRentalFrequency": "How often will they rent?",
    "requiredVehicleClasses": "Which vehicle classes are needed?",
    "licenseNumber": "License Number",
    "licenseAuthority": "License Authority",
    "licenseDate": "License Date",
    "idCardNumber": "ID Card Number",
    "idCardAuthority": "ID Card Authority",
    "idCardDate": "ID Card Date",
    "invalidCountry": "Invalid Country",
    "customerTier": "Customer tier",
    "sipId": "SIP ID (...{'@'}fpbx.de)",
    "did": "Direct dial",
    "callerId": "SIP Caller ID",
    "staffomaticUserId": "Staffomatic User ID"
  },
  "de": {
    "user": "Benutzer",
    "role": "Rolle",
    "partnerType": "Partnertyp",
    "customer": "Kunde",
    "createdBy": "Erstellt von",
    "gender": "Geschlecht",
    "firstName": "Vorname",
    "lastName": "Nachname",
    "company": "Firma",
    "vatId": "USt-IdNr.",
    "email": "Email",
    "city": "Stadt",
    "zip": "PLZ",
    "street": "Straße",
    "phone": "Telefon",
    "country": "Land",
    "dateOfBirth": "Geburtsdatum",
    "preferredLocal": "Bev. Sprache",
    "stations": "Stationen",
    "comment": "Kommentar",
    "commentDetailed": "Kommentar (Bitte beschreibe, wie Du den Kunden gewonnen hast und beschreibe Deine Geschichte mit dem Kunden kurz.)",
    "acquisitionSource": "Woher kommt der Kunde?",
    "intendedRentalFrequency": "Wie oft hat der Kunde vor zu mieten?",
    "requiredVehicleClasses": "Welche Fahrzeugklassen werden benötigt?",
    "licenseNumber": "Führerschein Nr.",
    "licenseAuthority": "Behörde",
    "licenseDate": "Ausstelldatum",
    "idCardNumber": "Ausweis Nr.",
    "idCardAuthority": "Behörde",
    "idCardDate": "Ausstelldatum",
    "invalidCountry": "Ungültiges Land",
    "customerTier": "Kundenrang",
    "sipId": "SIP ID (...{'@'}fpbx.de)",
    "did": "Interne Durchwahl",
    "callerId": "SIP Rufnummer",
    "staffomaticUserId": "Staffomatic Benutzer-ID"
  },
  "es": {
    "user": "Usuario",
    "role": "Rol",
    "partnerType": "Tipo de partner",
    "agencyType": "Tipo de partner",
    "customer": "Cliente",
    "createdBy": "Creado por",
    "gender": "Género",
    "firstName": "Nombre",
    "lastName": "Apellidos",
    "company": "Empresa",
    "email": "Correo electrónico",
    "city": "Ciudad",
    "zip": "Código postal",
    "street": "Calle",
    "phone": "Teléfono",
    "country": "País",
    "dateOfBirth": "Fecha de nacimiento",
    "preferredLocal": "Idioma preferido",
    "stations": "Estaciones",
    "comment": "Comentario",
    "commentDetailed": "Comentario (¿cómo consiguió a este cliente? Breve historia)",
    "acquisitionSource": "¿De dónde procede el cliente?",
    "intendedRentalFrequency": "¿Con qué frecuencia alquilará?",
    "requiredVehicleClasses": "¿Qué categorías de vehículo necesita?",
    "licenseNumber": "N.º permiso de conducir",
    "licenseAuthority": "Autoridad expedidora",
    "licenseDate": "Fecha de expedición",
    "idCardNumber": "N.º documento de identidad",
    "idCardAuthority": "Autoridad (DNI)",
    "idCardDate": "Fecha de expedición (DNI)",
    "invalidCountry": "País no válido",
    "customerTier": "Categoría de cliente",
    "sipId": "SIP ID (...{'@'}fpbx.de)",
    "did": "Extensión directa",
    "callerId": "Caller ID SIP",
    "staffomaticUserId": "ID de usuario Staffomatic"
  }
}
</i18n>
